import type { FC } from 'react';
import { useCallback, useContext, useEffect, useRef, useMemo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import GalleryMultiselect from '@wix/da-submit-shared/pkg/common/GalleryMultiselect';
import { GallerySelectItem } from '@wix/da-submit-shared/pkg/common/GalleryMultiselect/types';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import DesktopBatchActionWrapper from '../DesktopBatchActionWrapper';
import MobileBatchActionWrapper from '../MobileBatchActionWrapper';
import { BatchActionProps } from '../types';

export interface Props extends BatchActionProps {
  galleries?: PapiGallection[];
  isLoading?: boolean;
  batchLoadGalleries: () => void;
  batchEditGallery: (targetFolderId: number[]) => void;
}

const GalleryBatchEdit: FC<Props> = ({
  selectionArea,
  galleries,
  isLoading,
  batchEditGallery,
  batchLoadGalleries,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const selectedFolderIdsRef = useRef<number[]>([]);
  const inputId = useId();

  useEffect(() => {
    if (!galleries && !isLoading) {
      batchLoadGalleries();
    }
  }, [galleries, isLoading, batchLoadGalleries]);

  const handleSave = useCallback(() => {
    batchEditGallery(selectedFolderIdsRef.current);
  }, [batchEditGallery]);

  const items: GallerySelectItem[] = useMemo(
    () =>
      (galleries || []).map(gallery => ({
        gallery,
        value: `${gallery.folderId}`,
        selected: false,
      })),
    [galleries]
  );

  const form = isLoading ? (
    <LoadingIndicator view="llama" />
  ) : (
    <div>
      <GalleryMultiselect
        id={inputId}
        label="Gallery"
        placeholder={t('studio.batchActions.form.edit_gallery.placeholder')}
        items={items}
        onChange={(_, folderIds) => (selectedFolderIdsRef.current = folderIds)}
        isLabelHidden
        withFloatingList
      />
    </div>
  );

  if (isMobile) {
    return (
      <MobileBatchActionWrapper
        heading={t('studio.batchActions.subtitle.edit_gallery')}
        onSave={handleSave}
        restrictSave="non_core_multiple"
      >
        {form}
      </MobileBatchActionWrapper>
    );
  }
  return (
    <DesktopBatchActionWrapper
      heading={t('studio.batchActions.subtitle.edit_gallery')}
      selectionArea={selectionArea}
      onSave={handleSave}
      restrictSave="non_core_multiple"
    >
      {form}
    </DesktopBatchActionWrapper>
  );
};
GalleryBatchEdit.displayName = 'GalleryBatchEdit';

export default GalleryBatchEdit;
